/**
 * Парсинг #RGB / #RRGGBB в компоненты 0–255.
 */
export function parseHexColor(hex: string): { r: number, g: number, b: number } | null {
  const raw = hex.trim().replace('#', '')
  if (!raw) {
    return null
  }
  let h = raw
  if (h.length === 3) {
    h = h.split('').map(c => c + c).join('')
  }
  if (h.length !== 6) {
    return null
  }
  const n = Number.parseInt(h, 16)
  if (Number.isNaN(n)) {
    return null
  }
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}

/**
 * Относительная яркость (WCAG), 0 — чёрный, 1 — белый.
 */
export function getHexRelativeLuminance(hex: string): number | null {
  const rgb = parseHexColor(hex)
  if (!rgb) {
    return null
  }
  const { r, g, b } = rgb
  const toLin = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  const R = toLin(r)
  const G = toLin(g)
  const B = toLin(b)
  return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

/**
 * true — фон считается тёмным, на нём лучше светлый текст.
 */
export function isHexColorDark(hex: string, threshold = 0.45): boolean {
  const L = getHexRelativeLuminance(hex)
  if (L === null) {
    return false
  }
  return L < threshold
}

/**
 * Слишком светлый акцент: на светлой подложке как цвет заголовка нечитаем (белый на белом).
 */
export function isHexColorLight(hex: string, threshold = 0.72): boolean {
  const L = getHexRelativeLuminance(hex)
  if (L === null) {
    return false
  }
  return L >= threshold
}

const HEX_ICON_FALLBACK_LIGHT = '#FFFFFF'
const HEX_ICON_FALLBACK_DARK = '#333333'

/**
 * Цвет иконки на фоне модуля (оба значения — hex). Не-hex возвращаются как есть.
 */
export function resolveIconColorForModuleSurface(
  moduleColorHex: string,
  iconColorHex: string,
): string {
  if (!parseHexColor(moduleColorHex) || !parseHexColor(iconColorHex)) {
    return iconColorHex
  }
  if (isHexColorDark(moduleColorHex)) {
    if (isHexColorDark(iconColorHex)) {
      return HEX_ICON_FALLBACK_LIGHT
    }
    return iconColorHex
  }
  if (isHexColorLight(moduleColorHex) && isHexColorLight(iconColorHex)) {
    return HEX_ICON_FALLBACK_DARK
  }
  return iconColorHex
}

/**
 * Цвет крупной фоновой иконки (водяной знак). Альфа в цвете;
 * у слоя `.workspace-module__background` в этом режиме задаётся opacity: 1.
 */
export function resolveWatermarkIconColor(
  surface: 'dark-fill' | 'light-fill',
): string {
  switch (surface) {
    case 'dark-fill':
      return 'rgb(255 255 255 / 0.38)'
    case 'light-fill':
      return 'rgb(0 0 0 / 0.28)'
  }
}
